import { I18nPluralPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideChevronDown,
  lucideLink2,
  lucideLink2Off,
  lucideListFilter,
  lucideMinus,
  lucidePlus,
  lucideThumbsDown,
  lucideThumbsUp,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmToggleImports } from '@spartan-ng/helm/toggle';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { ChipFilteringComponent } from '../../../shared/components/chip-filtering/chip-filtering.component';
import { ChipComponent } from '../../../shared/components/chip/chip.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { ExpansionPanel } from '../../../shared/components/expansion-panel/expansion-panel.component';
import { InputSearchFilterComponent } from '../../../shared/components/input-search-filter/input-search-filter.component';
import { Activité } from '../../models/activité.model';
import { Sequence } from '../../models/séquence.model';
import { ActivitéStore } from '../../store/activité.store';
import { GlobalFormGroup } from '../séquence-form-form-groups.types';
import { SéquenceFormService } from '../séquence-form.service';
import { ReferentielStore } from '../../../administration/gestion-référentiels/store/referentiel.store';

@Component({
  selector: 'app-activites',
  imports: [
    HlmButtonImports,
    HlmIconImports,
    HlmTooltipImports,
    EmptyStateComponent,
    InputSearchFilterComponent,
    RouterModule,
    HlmSeparatorImports,
    ExpansionPanel,
    ChipComponent,
    I18nPluralPipe,
    ChipFilteringComponent,
    HlmToggleImports,
  ],
  templateUrl: './activites.component.html',
  providers: [
    provideIcons({
      lucideLink2,
      lucideLink2Off,
      lucideThumbsUp,
      lucideThumbsDown,
      lucideListFilter,
      lucideChevronDown,
      lucideCheck,
      lucidePlus,
      lucideMinus,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivitesComponent implements OnInit {
  readonly #referentielStore = inject(ReferentielStore);
  readonly #activiteStore = inject(ActivitéStore);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #destroy = inject(DestroyRef);
  readonly #sequenceFormService = inject(SéquenceFormService);
  readonly #change = inject(ChangeDetectorRef);

  readonly form = input.required<GlobalFormGroup>();

  readonly cartographie = input.required<Cartographie>();
  readonly sequence = input.required<Sequence>();

  readonly referentiels = linkedSignal(() => this.#referentielStore.entities());
  readonly activites = linkedSignal(() => this.#activiteStore.entities());

  readonly filtre = model<string>('');

  isPresentMap: Map<string, boolean> = new Map();

  protected readonly activiteMapping: { [k: string]: string } = {
    '=0': '# activité liée',
    '=1': '# activité liée',
    other: '# activités liées',
  };

  constructor() {
    effect(() => this.setActivtiésSélectionnées(this.form().value.activites ?? [], this.activites()));
  }

  ngOnInit(): void {
    this.#sequenceFormService.reload.pipe(takeUntilDestroyed(this.#destroy)).subscribe(() => {
      this.setActivtiésSélectionnées(this.form().value.activites ?? [], this.activites());
      this.#change.detectChanges();
    });
    this.#referentielStore.chargerParCartographie(this.cartographie().id);
  }

  toggleActivite(activité: Activité) {
    const current = this.form().value.activites ?? [];
    const index = current.findIndex((a) => a.id === activité.id);

    if (index === -1) {
      current.push(activité);
    } else {
      current.splice(index, 1);
    }
    this.form().controls.activites.setValue(current);
  }

  filtrer({ idReferentiel, filtre }: { filtre: string; idReferentiel?: string }) {
    if (filtre) {
      this.#activiteStore.charger({ idReferentiel, nom: filtre });
    }
  }

  setActivtiésSélectionnées(activitésSélectionnées: Activité[], activités: Activité[]) {
    this.isPresentMap.clear();
    activitésSélectionnées.forEach((a) => this.isPresentMap.set(a.id, activités.map((e) => e.id).includes(a.id)));
  }

  navigateToDetailsActivite(activiteId: string) {
    this.#router.navigate(['sequences', this.sequence().id, 'activités', activiteId], {
      relativeTo: this.#route,
    });
  }
}
